import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  type ToastProps,
} from './toast';

interface ToastShape {
  id: string;
  title?: string;
  description?: ReactNode;
  variant?: ToastProps['variant'];
  duration?: number;
}

interface ToastContextValue {
  toast: (input: Omit<ToastShape, 'id'>) => string;
  dismiss: (id: string) => void;
  toasts: ToastShape[];
}

const ToastContext = createContext<ToastContextValue | null>(null);

let counter = 0;

/**
 * Lightweight toast manager. Wrap the app once near the root. Hooks
 * call `useToast()` and dispatch `toast({ title, description, variant })`.
 *
 * Designed to be a thin layer over Radix Toast — we own the queue
 * (so multiple notifications stack visibly) and the variant -> color
 * mapping so call-sites stay declarative.
 */
export function ToastsProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastShape[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((input: Omit<ToastShape, 'id'>) => {
    const id = `toast-${++counter}`;
    setToasts((current) => [...current, { id, ...input }]);
    return id;
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({ toast, dismiss, toasts }),
    [toast, dismiss, toasts],
  );

  return (
    <ToastContext.Provider value={value}>
      <ToastProvider swipeDirection="right" duration={6000}>
        {children}
        {toasts.map((t) => (
          <Toast
            key={t.id}
            variant={t.variant}
            duration={t.duration}
            onOpenChange={(open) => {
              if (!open) dismiss(t.id);
            }}
          >
            <div className="flex flex-1 flex-col gap-1">
              {t.title ? <ToastTitle>{t.title}</ToastTitle> : null}
              {t.description ? (
                <ToastDescription>{t.description}</ToastDescription>
              ) : null}
            </div>
            <ToastClose />
          </Toast>
        ))}
        <ToastViewport />
      </ToastProvider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast() must be used inside <ToastsProvider />.');
  }
  return ctx;
}
